import React, { useState } from 'react';
import {
  X,
  ShieldCheck,
  FileDown,
  Copy,
  CheckCircle2,
  Clock,
  MapPin,
  MessageCircle,
  Camera,
  PenTool,
  AlertTriangle,
  UserCheck,
} from 'lucide-react';
import { episService } from '../../../services/epis.service';
import type { EpiEntregaContract } from '@sstexames/shared-types';

interface DetalhesEntregaModalProps {
  isOpen: boolean;
  onClose: () => void;
  entrega: EpiEntregaContract | null;
  onOpenAssinatura?: (entrega: EpiEntregaContract) => void;
}

export const DetalhesEntregaModal: React.FC<DetalhesEntregaModalProps> = ({
  isOpen,
  onClose,
  entrega,
  onOpenAssinatura,
}) => {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedHash, setCopiedHash] = useState(false);

  if (!isOpen || !entrega) return null;

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      await episService.downloadPdfFile(
        entrega.id,
        `ficha_epi_${entrega.id}_${entrega.colaborador_nome.replace(/\s+/g, '_')}.pdf`,
        entrega.empresa_id,
      );
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const copyToClipboard = (text: string, isHash: boolean = false) => {
    navigator.clipboard.writeText(text);
    if (isHash) {
      setCopiedHash(true);
      setTimeout(() => setCopiedHash(false), 2000);
    } else {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const shareViaWhatsApp = () => {
    if (!entrega.link_assinatura_publico) return;
    const msg = encodeURIComponent(
      `Olá ${entrega.colaborador_nome}, acesse o link seguro para assinar digitalmente o recebimento dos seus EPIs (NR-06):\n${entrega.link_assinatura_publico}`,
    );
    window.open(`https://api.whatsapp.com/send?text=${msg}`, '_blank');
  };

  const hasGeo = entrega.geo_latitude !== null && entrega.geo_latitude !== undefined &&
                 entrega.geo_longitude !== null && entrega.geo_longitude !== undefined;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-8">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center border border-primary-100">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900">
                  Ficha de Entrega de EPI #{entrega.id}
                </h2>
                {entrega.status === 'entregue' ? (
                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Assinada & Concluída
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Pendente de Assinatura
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Emitida em {new Date(entrega.data_entrega).toLocaleDateString('pt-BR')} &bull; Método:{' '}
                {entrega.metodo_assinatura === 'link_celular' ? 'Link Celular' : 'Presencial Portal'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Card: Dados do Colaborador & Empresa */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50/70 p-4 rounded-xl border border-gray-200/80 text-xs">
            <div>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">
                Colaborador Recebedor
              </span>
              <p className="text-sm font-bold text-gray-900 mt-0.5">{entrega.colaborador_nome}</p>
              <div className="mt-1.5 space-y-0.5 text-gray-600 font-medium">
                <p>CPF: <span className="font-mono text-gray-800">{entrega.colaborador_cpf}</span></p>
                {entrega.colaborador_rg && <p>RG: {entrega.colaborador_rg}</p>}
                {entrega.colaborador_matricula && <p>Matrícula eSocial: {entrega.colaborador_matricula}</p>}
                <p>Cargo: {entrega.cargo_nome}</p>
                <p>Setor: {entrega.setor_nome || 'Geral'}</p>
              </div>
            </div>

            <div>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">
                Informações da Empresa / Emissor
              </span>
              <p className="text-sm font-bold text-gray-900 mt-0.5">{entrega.empresa_nome || 'Empresa Emissora'}</p>
              <div className="mt-1.5 space-y-0.5 text-gray-600 font-medium">
                <p>Data do Registro: {new Date(entrega.data_entrega).toLocaleDateString('pt-BR')}</p>
                <p>Versão Matriz Aplicada: {entrega.matriz_versao_id ? `Versão #${entrega.matriz_versao_id}` : 'Regulamentar'}</p>
                {entrega.observacoes && (
                  <p className="mt-1 text-gray-500 italic">Obs: {entrega.observacoes}</p>
                )}
              </div>
            </div>
          </div>

          {/* Card: Evidências e Trilha de Auditoria (NR-06) */}
          <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-gray-900 flex items-center gap-1.5 uppercase tracking-wider">
                <UserCheck className="w-4 h-4 text-emerald-600" /> Trilha de Auditoria & Integridade Jurídica
              </h3>
              {entrega.hash_integridade && (
                <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                  SHA-256 Validado
                </span>
              )}
            </div>

            {/* Hash & IP & Localização */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs bg-gray-50 p-3 rounded-lg">
              <div>
                <span className="text-[10px] text-gray-400 block font-semibold">Assinado em</span>
                <span className="font-bold text-gray-800">
                  {entrega.assinado_em
                    ? new Date(entrega.assinado_em).toLocaleString('pt-BR')
                    : 'Aguardando Assinatura'}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block font-semibold">Endereço IP</span>
                <span className="font-mono text-gray-800">{entrega.ip_assinatura || 'Não registrado'}</span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block font-semibold">Geolocalização</span>
                {hasGeo ? (
                  <a
                    href={`https://www.google.com/maps?q=${entrega.geo_latitude},${entrega.geo_longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:underline flex items-center gap-1 font-medium"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {Number(entrega.geo_latitude).toFixed(4)}, {Number(entrega.geo_longitude).toFixed(4)}
                  </a>
                ) : (
                  <span className="text-gray-400">Não capturada</span>
                )}
              </div>
            </div>

            {/* Hash SHA-256 */}
            {entrega.hash_integridade && (
              <div className="bg-gray-50 p-3 rounded-lg flex items-center justify-between gap-2">
                <div className="overflow-hidden">
                  <span className="text-[10px] text-gray-400 block font-semibold">Hash de Integridade NR-06</span>
                  <p className="font-mono text-[11px] text-gray-800 truncate" title={entrega.hash_integridade}>
                    {entrega.hash_integridade}
                  </p>
                </div>
                <button
                  onClick={() => copyToClipboard(entrega.hash_integridade || '', true)}
                  className="px-2.5 py-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-medium flex items-center gap-1 shadow-2xs flex-shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copiedHash ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            )}

            {/* Galeria de Fotos e Assinatura */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {/* Foto RH */}
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 text-center">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-2 flex items-center justify-center gap-1">
                  <Camera className="w-3.5 h-3.5" /> Foto do Entregador (RH)
                </span>
                {entrega.foto_rh_url ? (
                  <img
                    src={entrega.foto_rh_url}
                    alt="Foto do Responsável RH"
                    className="w-full h-36 object-cover rounded-lg border border-gray-200 mx-auto"
                  />
                ) : (
                  <div className="w-full h-36 rounded-lg bg-gray-200/60 flex items-center justify-center text-gray-400 text-xs">
                    Sem foto do RH
                  </div>
                )}
              </div>

              {/* Foto Colaborador */}
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 text-center">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-2 flex items-center justify-center gap-1">
                  <Camera className="w-3.5 h-3.5" /> Biometria Facial Colaborador
                </span>
                {entrega.foto_colaborador_url ? (
                  <img
                    src={entrega.foto_colaborador_url}
                    alt="Selfie Colaborador"
                    className="w-full h-36 object-cover rounded-lg border border-gray-200 mx-auto"
                  />
                ) : (
                  <div className="w-full h-36 rounded-lg bg-gray-200/60 flex flex-col items-center justify-center text-gray-400 text-xs gap-1">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <span>Pendente de captura</span>
                  </div>
                )}
              </div>

              {/* Assinatura Colaborador */}
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 text-center">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-2 flex items-center justify-center gap-1">
                  <PenTool className="w-3.5 h-3.5" /> Assinatura na Tela
                </span>
                {entrega.assinatura_colaborador_url ? (
                  <img
                    src={entrega.assinatura_colaborador_url}
                    alt="Assinatura Digital"
                    className="w-full h-36 object-contain bg-white rounded-lg border border-gray-200 mx-auto p-2"
                  />
                ) : (
                  <div className="w-full h-36 rounded-lg bg-gray-200/60 flex flex-col items-center justify-center text-gray-400 text-xs gap-1">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <span>Pendente de assinatura</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabela de Itens Entregues */}
          <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-2xs space-y-3">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
              Equipamentos de Proteção Individual Entregues ({entrega.itens.length})
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2">Equipamento</th>
                    <th className="pb-2">CA</th>
                    <th className="pb-2">Fabricante</th>
                    <th className="pb-2 text-center">Qtd</th>
                    <th className="pb-2">Tamanho/Lote</th>
                    <th className="pb-2">Previsão Troca</th>
                    <th className="pb-2 text-right">Origem Regra</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entrega.itens.map((it) => (
                    <tr key={it.id} className="hover:bg-gray-50/60">
                      <td className="py-2.5 font-bold text-gray-900">{it.descricao_epi}</td>
                      <td className="py-2.5 font-mono text-gray-700">{it.ca_numero || 'Certificado'}</td>
                      <td className="py-2.5 text-gray-600">{it.fabricante || '—'}</td>
                      <td className="py-2.5 text-center font-bold text-gray-900">{it.quantidade}</td>
                      <td className="py-2.5 text-gray-600">
                        {it.tamanho ? `Tam: ${it.tamanho}` : ''}{it.lote ? ` (Lote: ${it.lote})` : ''}
                        {!it.tamanho && !it.lote ? '—' : ''}
                      </td>
                      <td className="py-2.5 text-gray-700 font-medium">
                        {it.data_prevista_troca
                          ? new Date(it.data_prevista_troca).toLocaleDateString('pt-BR')
                          : 'Conforme Desgaste'}
                      </td>
                      <td className="py-2.5 text-right">
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                          {it.regra_origem_tipo}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Link público pendente se aplicável */}
          {entrega.status === 'pendente_assinatura' && entrega.link_assinatura_publico && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="font-bold text-amber-900">
                  Assinatura pendente via Link de Autoatendimento Celular
                </span>
              </div>
              <p className="text-amber-800 text-[11px]">
                O colaborador pode escanear o QR code ou abrir o link direto para fotografar o rosto e assinar:
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={entrega.link_assinatura_publico}
                  className="flex-1 bg-white border border-amber-300 rounded-lg px-3 py-1.5 text-xs text-amber-950 font-mono shadow-2xs"
                />
                <button
                  onClick={() => copyToClipboard(entrega.link_assinatura_publico || '')}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold shadow-2xs flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" /> {copiedLink ? 'Copiado!' : 'Copiar Link'}
                </button>
                <button
                  onClick={shareViaWhatsApp}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold shadow-2xs flex items-center gap-1"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-colors"
            >
              <FileDown className="w-4 h-4" />
              {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF Oficial NR-06'}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {entrega.status === 'pendente_assinatura' && onOpenAssinatura && (
              <button
                onClick={() => {
                  onClose();
                  onOpenAssinatura(entrega);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-colors"
              >
                <PenTool className="w-4 h-4" /> Assinar Presencialmente Agora
              </button>
            )}

            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
